import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { RentenCheck } from '../components/RentenCheck';
import { AltersvorsorgeKategorien } from '../components/AltersvorsorgeKategorien';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Footer } from '../components/legal';

/**
 * Renten-Check Seite
 * Fokussiert auf Rentenlücke und Altersvorsorge
 */

const RentenCheckPage = () => {
  const navigate = useNavigate();

  const faqItems = [
    {
      question: 'Warum muss ich mich jetzt schon darum kümmern?',
      answer: (
        <>
          Je später du anfängst, desto mehr musst du monatlich zurücklegen, um dieselbe Lücke zu schließen, und desto weniger Zeit hat dein Kapital zum Wachsen. Auch die Bundesregierung sieht die gesetzliche Rente nur als Fundament, das durch private Vorsorge ergänzt werden muss.{' '}
          <a 
            href="https://www.bundesfinanzministerium.de/Content/DE/FAQ/reform-der-privaten-altersvorsorge.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cobalt hover:text-navy font-medium underline underline-offset-2"
          >
            Mehr dazu beim Bundesfinanzministerium →
          </a>
        </>
      ),
    },
    {
      question: 'Berätst du mich zur kompletten Altersvorsorge oder nur zu einzelnen Produkten?',
      answer: 'Ich berate dich zu deiner gesamten Situation, inklusive Einordnung deiner gesetzlichen Rente. Bei fondsgebundener Rentenversicherung und Basisrente vermittle ich direkt.',
    },
    {
      question: 'Was ist eine Basisrente (Rürup) und für wen lohnt sie sich?',
      answer: 'Sie eignet sich besonders für Selbstständige und Gutverdiener, die in der Ansparphase hohe Steuervorteile nutzen wollen. Ob sie zu dir passt, hängt von deiner individuellen Steuersituation ab. Das klären wir im Gespräch.',
    },
    {
      question: 'Kann ich mehrere Wege gleichzeitig nutzen?',
      answer: 'Ja, viele kombinieren zum Beispiel eine Basisrente mit einem flexiblen ETF-Sparplan. Was sinnvoll ist, hängt von deiner persönlichen Situation ab.',
    },
  ];

  return (
    <div className="min-h-screen bg-ice">
      <Navigation variant="light" />

      {/* Hero + Calculator in one section - similar to /absicherungscheck */}
      <div className="pt-24 sm:pt-28">
        <RentenCheck 
          showHeader={true}
          headerConfig={{
            eyebrow: 'RENTEN-CHECK',
            headline: 'Wie groß ist deine Rentenlücke?',
            subline: 'Finde heraus, wie viel dir im Alter monatlich fehlen könnte, und was du jetzt noch ändern kannst.',
          }}
        />
      </div>

      {/* FAQ Section - direkt unter dem Rechner */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-8 md:mb-12">
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate mb-3">Häufige Fragen</div>
            <h3 className="font-playfair text-xl sm:text-2xl md:text-3xl font-bold text-navy tracking-tight">
              Das werde ich zur Altersvorsorge gefragt
            </h3>
          </div>
          
          <Accordion type="single" collapsible className="space-y-3">
            {faqItems.map((item, i) => (
              <AccordionItem 
                key={i} 
                value={`item-${i}`} 
                className="bg-bone rounded-xl border border-black/5 shadow-sm px-5 data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="hover:no-underline py-5">
                  <span className="font-outfit text-sm sm:text-base font-semibold text-navy text-left pr-4">
                    {item.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-xs sm:text-sm text-charcoal leading-relaxed pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Drei Töpfe für die Altersvorsorge */}
      <AltersvorsorgeKategorien />

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-navy">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <p className="text-cobalt text-sm font-medium mb-3">Du bekommst, was zu dir passt.</p>
          <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
            Bereit für Klarheit?
          </h2>
          <p className="text-white/60 text-base mb-8 max-w-lg mx-auto">
            Im kostenlosen Erstgespräch schauen wir, ob und wie du deine Rentenlücke schließen kannst.
          </p>
          <button
            onClick={() => navigate('/strategiegespraech')}
            className="group inline-flex items-center gap-2 px-6 sm:px-8 py-3.5 bg-white text-navy rounded-xl font-semibold hover:bg-bone transition-all shadow-lg"
            data-testid="rentencheck-cta-btn"
          >
            Gespräch vereinbaren
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer variant="dark" />
    </div>
  );
};

export default RentenCheckPage;
